import { useState, useEffect, useCallback, useRef } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { useAlert } from 'react-alert';
import { useLocation } from 'react-router-dom';
import { getMyCreditBalance } from '../graphql/credits/queries';
import { useCredits as useCreditsMutationGQL } from '../graphql/credits/mutations';
import { onUpdateUserCredits } from '../graphql/credits/subscriptions';

// Credit operation constants
export const CreditOperation = {
  VISUAL_SEARCH: 'VISUAL_SEARCH',
  PRICE_MONITORING: 'PRICE_MONITORING',
  AI_ENHANCEMENT: 'AI_ENHANCEMENT',
  DATA_EXPORT: 'DATA_EXPORT',
  API_USAGE: 'API_USAGE',
};

// Credit costs for operations
export const CreditCosts = {
  [CreditOperation.VISUAL_SEARCH]: 1,
  [CreditOperation.PRICE_MONITORING]: 0.2, // Variable based on frequency
  [CreditOperation.AI_ENHANCEMENT]: 3,
  [CreditOperation.DATA_EXPORT]: 5,
  [CreditOperation.API_USAGE]: 0.1,
};

export function useCredits() {
  const alert = useAlert();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(() => {
    const cached = localStorage.getItem('creditBalance');
    return cached ? parseInt(cached, 10) : null;
  });
  const [subscriptionTier, setSubscriptionTier] = useState(() => {
    return localStorage.getItem('subscriptionTier') || 'FREE';
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState('ACTIVE');
  const isMountedRef = useRef(true);
  const lastPathRef = useRef(location.pathname);

  const fetchCreditBalance = useCallback(async () => {
    if (!isMountedRef.current) return;

    setLoading(true);
    try {
      const { data } = await API.graphql(graphqlOperation(getMyCreditBalance));
      const creditData = data?.getMyCreditBalance;

      if (!isMountedRef.current) return;

      if (creditData) {
        const newBalance = creditData.total || 0;
        const newTier = creditData.subscriptionTier || 'FREE';

        setBalance(newBalance);
        setSubscriptionTier(newTier);
        setSubscriptionStatus('ACTIVE'); // Default status since not in schema

        // Cache values in localStorage for smooth reloads
        localStorage.setItem('creditBalance', newBalance.toString());
        localStorage.setItem('subscriptionTier', newTier);
      } else {
        setBalance(0);
        setSubscriptionTier('FREE');

        // Clear cache when no data
        localStorage.setItem('creditBalance', '0');
        localStorage.setItem('subscriptionTier', 'FREE');
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      // Set fallback values on error only if no cached values exist
      if (balance === null) {
        setBalance(0);
        setSubscriptionTier('FREE');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchCreditBalance();

    // Set up the subscription for credit updates
    let subscription;

    const setupSubscription = async () => {
      try {
        subscription = API.graphql(graphqlOperation(onUpdateUserCredits)).subscribe({
          next: ({ value }) => {
            if (!isMountedRef.current) return;

            const creditData = value?.data?.onUpdateUserCredits;
            if (creditData) {
              const newBalance = creditData.balance || 0;
              const newTier = creditData.subscriptionTier || 'FREE';

              setBalance(newBalance);
              setSubscriptionTier(newTier);
              setSubscriptionStatus(creditData.subscriptionStatus || 'ACTIVE');

              // Update cache when subscription updates
              localStorage.setItem('creditBalance', newBalance.toString());
              localStorage.setItem('subscriptionTier', newTier);
            }
          },
          error: error => {
            console.error('Credit subscription error:', error);
            // Subscription errors shouldn't prevent normal operation
          },
        });
      } catch (error) {
        console.error('Failed to set up credit subscription:', error);
        // Continue without subscription if it fails
      }
    };
    setupSubscription();
    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [fetchCreditBalance]); // Refresh credit balance when navigating between pages
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      // Add a small delay to ensure any credit operations from the previous page are completed
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          fetchCreditBalance();
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // Always return a function or undefined to satisfy consistent-return
    return undefined;
  }, [location.pathname, fetchCreditBalance]);
  const checkCredits = (operation, required = null) => {
    const cost = required || CreditCosts[operation] || 1;
    return balance >= cost;
  };
  const consumeCredits = async (operation, amount = null, referenceId = null) => {
    const cost = amount || CreditCosts[operation] || 1;
    if (!checkCredits(operation, cost)) {
      throw new Error('Insufficient credits');
    }
    try {
      const { data } = await API.graphql(
        graphqlOperation(useCreditsMutationGQL, {
          operation,
          amount: cost,
          referenceId,
        }),
      );
      if (!data.useCredits.success) {
        throw new Error(data.useCredits.error);
      }
      // Update balance after successful consumption only if component is still mounted
      if (isMountedRef.current) {
        await fetchCreditBalance();
      }
      return data.useCredits;
    } catch (error) {
      throw error;
    }
  };
  const showInsufficientCreditsAlert = operation => {
    const cost = CreditCosts[operation] || 1;
    alert.error(`Insufficient credits. You need ${cost} credits to perform this action.`);
  };
  return {
    balance,
    subscriptionTier,
    subscriptionStatus,
    loading,
    checkCredits,
    consumeCredits,
    showInsufficientCreditsAlert,
    refetch: fetchCreditBalance,
  };
}
